import app from 'flarum/forum/app';
import Link from 'flarum/common/components/Link';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Placeholder from 'flarum/common/components/Placeholder';
import extractText from 'flarum/common/utils/extractText';
import AutoReloadingPage from './AutoReloadingPage';
import SocietyEditModal from './SocietyEditModal';
import ToxicityEditModal from './ToxicityEditModal';
import SocietyMembersSection from './SocietyMembersSection';
import CardLayoutEditor from './CardLayoutEditor';
import { loginRequiredPlaceholder } from '../utils/requireLogin';
/**
 * /societies/:id - one société's own settings (name), its toxicity list,
 * and its card layout. Its exhibitions are deliberately NOT listed here
 * any more - that was a plain duplicate of what /exhibitions already shows
 * per-société (ExhibitionsListPage's own sections, which link their
 * société name to this exact page) - kept only "Sociétés mycologiques" as
 * a back-link to that list. The cartothèque itself moved to its own
 * CartothequePage (/societies/:id/cartotheque, linked from the header
 * button below) per explicit feedback - this page was getting crowded, and
 * the cartothèque needs its own direct link from ExhibitionPage too (for
 * creating a card on the spot while logging a finding that isn't carded
 * yet).
 */
export default class SocietyPage extends AutoReloadingPage {
  society = null;
  toxicities = null;
  resetData() {
    this.society = null;
    this.toxicities = null;
  }
  currentId() {
    return this.society && this.society.id();
  }

  // SocietyResource's own Show endpoint is ->authenticated() - a guest's
  // doomed request is already skipped one level up, in AutoReloadingPage's
  // own load() (see requireLogin.js's own docblock).
  fetch() {
    return app.store.find('champis-exhibitions-societies', m.route.param('id'));
  }
  onLoaded(society) {
    this.society = society;
    app.setTitle(society.name());
    m.redraw();
    this.loadToxicities();
  }
  loadToxicities() {
    this.toxicities = null;
    m.redraw();
    app.store.find('champis-exhibitions-toxicities', {
      filter: {
        society: this.society.id()
      },
      sort: 'name'
    }).then(toxicities => {
      this.toxicities = toxicities;
      m.redraw();
    });
  }
  view() {
    const t = function (key, params) {
      if (params === void 0) {
        params = {};
      }
      return app.translator.trans(`champis-exhibitions.forum.society_page.${key}`, params);
    };
    if (this.requiresLogin) {
      return m("div", {
        className: "Page ChampisSocietyPage"
      }, m("div", {
        className: "container"
      }, loginRequiredPlaceholder(t('login_required'))));
    }
    if (this.error) {
      return m("div", {
        className: "Page ChampisSocietyPage"
      }, m("div", {
        className: "container"
      }, m(Placeholder, {
        text: t('error')
      })));
    }
    if (!this.society) {
      return m("div", {
        className: "Page ChampisSocietyPage"
      }, m("div", {
        className: "container"
      }, m(LoadingIndicator, null)));
    }
    return m("div", {
      className: "Page ChampisSocietyPage"
    }, m("div", {
      className: "container"
    }, m("div", {
      className: "ChampisDiscussionList-header"
    }, m(Link, {
      href: app.route('exhibitions'),
      className: "ChampisExhibitions-backLink ChampisExhibitions-backLink--prominent"
    }, m("i", {
      className: "icon fas fa-chevron-left",
      "aria-hidden": "true"
    }), " ", t('back_to_exhibitions')), m("div", {
      className: "ChampisExhibitions-headerRow"
    }, m("div", null, m("i", {
      className: "icon fas fa-users",
      "aria-hidden": "true"
    }), " ", this.society.name()), m("div", {
      className: "ChampisSocietyPage-sectionHeaderActions"
    }, m(Button, {
      icon: "fas fa-pencil-alt",
      className: "Button",
      onclick: () => this.openEditModal()
    }, t('edit_button'))))), m("div", {
      className: "ChampisSocietyPage-cartothequeBlock"
    }, m(Link, {
      href: app.route('cartotheque', {
        id: this.society.id()
      }),
      className: "Button"
    }, m("i", {
      className: "icon fas fa-address-card",
      "aria-hidden": "true"
    }), " ", t('cartotheque_button'))), [m(SocietyMembersSection, {
      key: this.society.id(),
      society: this.society
    })], this.toxicitiesSection(), this.cardLayoutSection()));
  }
  toxicitiesSection() {
    const t = function (key, params) {
      if (params === void 0) {
        params = {};
      }
      return app.translator.trans(`champis-exhibitions.forum.society_page.${key}`, params);
    };
    return m("div", {
      className: "ChampisSocietyPage-section"
    }, m("div", {
      className: "ChampisSocietyPage-sectionHeader"
    }, m("h3", null, t('toxicities_title')), m(Button, {
      icon: "fas fa-plus",
      className: "Button Button--primary",
      onclick: () => this.openAddToxicityModal()
    }, t('add_toxicity_button'))), m("div", {
      className: "ChampisSocietyPage-sectionBody"
    }, this.toxicities === null ? m(LoadingIndicator, null) : this.toxicities.length === 0 ? m(Placeholder, {
      text: t('toxicities_empty')
    }) : m("ul", {
      className: "ChampisSocietyPage-toxicities"
    }, this.toxicities.map(toxicity => this.toxicityRow(toxicity)))));
  }
  toxicityRow(toxicity) {
    const t = key => app.translator.trans(`champis-exhibitions.forum.society_page.${key}`);
    return m("li", {
      key: toxicity.id(),
      className: "ChampisSocietyPage-toxicity"
    }, toxicity.svgUrl() && m("img", {
      className: "ChampisSocietyPage-toxicityIcon",
      src: toxicity.svgUrl(),
      alt: ""
    }), m("span", {
      className: "ChampisSocietyPage-toxicityName"
    }, toxicity.name()), m("div", {
      className: "ChampisSocietyPage-rowActions"
    }, m("a", {
      "aria-label": extractText(t('edit_toxicity_button')),
      title: extractText(t('edit_toxicity_button')),
      onclick: () => this.openEditToxicityModal(toxicity)
    }, m("i", {
      className: "icon fas fa-pencil-alt",
      "aria-hidden": "true"
    })), m("a", {
      "aria-label": extractText(t('delete_toxicity_button')),
      title: extractText(t('delete_toxicity_button')),
      onclick: () => this.deleteToxicity(toxicity)
    }, m("i", {
      className: "icon fas fa-trash",
      "aria-hidden": "true"
    }))));
  }
  cardLayoutSection() {
    const t = function (key, params) {
      if (params === void 0) {
        params = {};
      }
      return app.translator.trans(`champis-exhibitions.forum.society_page.${key}`, params);
    };
    return m("div", {
      className: "ChampisSocietyPage-section"
    }, m("div", {
      className: "ChampisSocietyPage-sectionHeader"
    }, m("h3", null, t('card_layout_title'))), m("div", {
      className: "ChampisSocietyPage-sectionBody"
    }, m(CardLayoutEditor, {
      society: this.society
    })));
  }
  openEditModal() {
    app.modal.show(SocietyEditModal, {
      society: this.society,
      onsaved: () => m.redraw()
    });
  }
  openAddToxicityModal() {
    app.modal.show(ToxicityEditModal, {
      society: this.society,
      onsaved: () => this.loadToxicities()
    });
  }
  openEditToxicityModal(toxicity) {
    app.modal.show(ToxicityEditModal, {
      society: this.society,
      toxicity,
      onsaved: () => this.loadToxicities()
    });
  }
  deleteToxicity(toxicity) {
    const t = key => app.translator.trans(`champis-exhibitions.forum.society_page.${key}`);
    if (!confirm(extractText(t('delete_toxicity_confirm')))) return;
    toxicity.delete().then(() => {
      this.loadToxicities();
    });
  }
}
flarum.reg.add('champis-exhibitions', 'forum/components/SocietyPage', SocietyPage);